(ns web3.routing.utils
  (:require [applied-science.js-interop :as j]
            [clojure.string :as str]
            [goog.events :as events]
            [web3.utils :as utils])
  (:import
   [goog History]
   [goog.history Html5History]
   [goog Uri]))


;; From http://www.lispcast.com/mastering-client-side-routing-with-secretary-and-goog-history
;; Replaces this method: https://closure-library.googlecode.com/git-history/docs/local_closure_goog_history_html5history.js.source.html#line237
;; Without this patch, google closure does not handle changes to query parameters.
(set! (.. ^js Html5History -prototype -getUrl_)
      (fn [token]
        (this-as ^js this
          (if (.-useFragment_ this)
            (str "#" token)
            (str (.-pathPrefix_ this) token)))))

(def browser? (exists? js/window))
(def history-support? (when browser? (.isSupported Html5History)))

(defn get-route
  "In a browsing environment, reads the current location."
  []
  (if history-support?
    (str js/window.location.pathname js/window.location.search)
    (if (= js/window.location.pathname "/")
      (.substring js/window.location.hash 1)
      (str js/window.location.pathname js/window.location.search))))

(defn- make-history
  "Set up browser history, using HTML5 history if available."
  []
  (when browser?
    (if history-support?
      (doto (Html5History.)
        (.setPathPrefix (str js/window.location.protocol
                             "//"
                             js/window.location.host))
        (.setUseFragment false))
      (if (not= "/" js/window.location.pathname)
        (aset js/window "location" (str "/#" (get-route)))
        (History.)))))

(defonce history
         (some-> (make-history)
                 (doto (.setEnabled true))))

(defn segments
  "Splits route into segments, ignoring leading and trailing slashes."
  [route]
  (let [segments (-> route
                     (str/replace #"[#?].*" "")
                     (str/split \/ -1))
        segments (cond-> segments
                         (= "" (first segments)) (subvec 1))]
    (cond-> segments
            (= "" (last segments)) (pop))))

(comment (assert (= (segments "/") []))
         (assert (= (segments "//") [""]))
         (assert (= (segments "///") ["" ""]))
         (assert (= (segments "/a/b")
                    (segments "a/b/")
                    (segments "a/b") ["a" "b"])))

(defn query
  "Returns query parameters as map."
  [path]
  (let [data (.getQueryData (Uri. path))]
    (reduce (fn [m k]
              (assoc m (keyword k) (.get data k))) {} (.getKeys data))))

(defn parse-path
  "Returns map of parsed location information for path."
  [path]
  {:path     path
   :segments (segments path)
   :query    (query path)})

(defn- remove-empty
  "Remove empty values/strings from map"
  [m]
  (reduce-kv (fn [m k v]
               (cond-> m
                       (#{nil ""} v) (dissoc k))) m m))

(defn query-string [m]
  (let [js-query (-> m
                     (remove-empty)
                     (clj->js))]
    (.. ^js Uri
        -QueryData
        (createFromMap js-query)
        (toString))))

(defn link?
  "Return true if element is a link"
  [el]
  (some-> el .-tagName (= "A")))

(defn external-link? [link]
  ;; check to see if we should let the browser handle the link
  ;; (eg. external link, or valid hash reference to an element on the page)
  (let [location (.-location js/window)]
    (or (not= (.-host location) (.-host link))
        (not= (.-protocol location) (.-protocol link))
        ;; if only the hash has changed, & element exists on page, allow browser to scroll there
        (and (.-hash link)
             (= (.-pathname location) (.-pathname link))
             (not= (.-hash location) (.-hash link))
             (.getElementById js/document (subs (.-hash link) 1))))))

(defn current-origin []
  ;; in IE/Edge, link elements do not expose an `origin` attribute
  (let [location ^js (.-location js/window)]
    (str (.-protocol location)
         "//"
         (.-host location))))

(defn relative-path [link]
  (str/replace (.-href link) (current-origin) ""))

(defn click-event-handler
  "Intercept clicks on links with valid pushstate hrefs. Callback is passed the link."
  [e callback]
  (when-let [link (utils/closest (.-target e) link?)]
    (when-not (external-link? link)
      (.preventDefault e)
      (callback (relative-path link)))))

(defn intercept-clicks
  ([callback] (intercept-clicks js/document callback))
  ([element callback]
   (events/listen element "click"
                  (fn [e]
                    (click-event-handler e callback)))))

(defn listen
  "Set up a listener on route changes. Options:

  fire-now? (boolean, `true`): executes listener immediately, with current parsed route.

  Returns a key which can be passed to `unlisten` to remove listener."
  ([listener]
   (listen listener {}))
  ([listener {:keys [fire-now?]
              :or   {fire-now? true}}]
   (when fire-now? (listener (parse-path (get-route))))
   (events/listen history "navigate" #(listener (parse-path (get-route))))))